import {
  Link,
  Avatar,
  Dropmenu,
  LabelGroup,
  SlideDown,
  Typography,
  FlexBox,
} from '@globalmed/atlas-ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { InstitutionLogo } from '../InstitutionLogo';
import { Notifications } from '../Notifications';
import { OnDemandNotifications } from '../OnDemandNotifications';
import styles from './TopPanel.module.css';
import { HelpInfoModal } from '@/components/HelpInfoModal';
import { RoleControl } from '@/components/RoleControl';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useInstitutionsStore } from '@/stores/institutions';
import { useUsersStore } from '@/stores/users';
import {
  EExternalLinks,
  ERouterNames,
  EUserRoles,
  IDropMenuItems,
} from '@/types';
import { getLinkToLogin } from '@/utils/getLinkToLogin';
import { getUserInitials, nameFormat } from '@/utils/valuesFormat';
import { eViewApi, getInstance } from '@globalmed/encounter-api';

export const TopPanel = () => {
  const { t } = useTranslation();
  const isSmallScreen = useMediaQuery('(max-width: 576px)');
  const isMediumScreen = useMediaQuery('(max-width: 1280px)');
  const navigate = useNavigate();
  const [helpInfoModalOpen, setHelpInfoModalOpen] = useState(false);
  const [isSlideDownOpen, setIsSlideDownOpen] = useState(false);

  const { userData, role, deviceId, logout } = useUsersStore((state) => ({
    userData: state.userData,
    role: state.role,
    deviceId: state.deviceId,
    logout: state.logout,
  }));
  const isDevice = role === EUserRoles.DEVICE;

  const disablePatientScheduling = useInstitutionsStore(
    (state) =>
      !!state.currentInstitution?.configuration?.disablePatientScheduling,
  );

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      navigate(getLinkToLogin());
    }
  };

  const handleCallHelpModal = () => {
    setHelpInfoModalOpen(true);
  };

  const allDropmenuItems: IDropMenuItems[] = [
    {
      key: 'account-settings',
      text: t('account-settings.account-settings'),
      icon: 'SettingsGear',
      onClick: () => {
        navigate(ERouterNames.ACCOUNT_SETTINGS);
      },
    },
    {
      key: 'help',
      text: t('help'),
      icon: 'InfoCircle',
      onClick: () => handleCallHelpModal(),
    },
    {
      key: 'export-logs',
      text: t('export-logs'),
      icon: 'Download',
      onClick: () => {
        const api = getInstance(eViewApi, {
          domain: import.meta.env.VITE_API_VIDEO_DOMAIN,
          peerConnectionConfiguration: {},
        });
        api.downloadConsoleLogs();
        api.downloadWebRtcLogs();
      },
      hidden: isMediumScreen,
    },
    {
      key: 'privacy-policy',
      text: t('privacy-policy'),
      icon: 'Policy',
      onClick: () => {
        window.open(EExternalLinks.PRIVACY_POLICY, '_blank');
      },
    },
    {
      key: 'line-1',
      text: 'line',
      onClick: () => {},
      hidden: isSmallScreen,
    },
    {
      key: 'logout',
      text: t('first-login.log-out'),
      icon: 'LogOut',
      onClick: handleLogout,
    },
  ];

  const dropmenuItems = allDropmenuItems.filter((item) => !item.hidden);

  const userInitials = getUserInitials(userData?.firstName, userData?.lastName);
  const userName = isDevice
    ? nameFormat(userData?.firstName, deviceId)
    : userData?.firstName &&
      nameFormat(userData?.firstName, userData?.lastName);

  return (
    <div className={styles.wrapper} data-testid="top-panel">
      <HelpInfoModal
        open={helpInfoModalOpen}
        onOpenChange={setHelpInfoModalOpen}
      />
      <InstitutionLogo />
      <div className={styles.rightGroup}>
        <RoleControl
          components={{
            [EUserRoles.PROVIDER]: !disablePatientScheduling ? (
              <OnDemandNotifications />
            ) : null,
          }}
          withoutNavigate
        />
        <div className={styles.bell}>
          <Notifications />
        </div>
        {!isSmallScreen ? (
          <Dropmenu
            placement="bottom-end"
            trigger={
              <Avatar
                src={userData?.meta?.profilePicture ?? ''}
                text={!userData?.meta?.profilePicture ? userInitials : ''}
                size="xl"
                withFocus
              />
            }
          >
            <Dropmenu.Header>
              <LabelGroup title={userName ?? ''} text={userData?.email ?? ''} />
            </Dropmenu.Header>
            <Dropmenu.Line />
            {dropmenuItems.map((item) => {
              if (item.text === 'line') {
                return <Dropmenu.Line key={item.key} />;
              }
              const { key, text, ...props } = item;
              return (
                <Dropmenu.Item key={key} {...props}>
                  {text}
                </Dropmenu.Item>
              );
            })}
          </Dropmenu>
        ) : (
          <>
            <Avatar
              src={userData?.meta?.profilePicture ?? ''}
              text={!userData?.meta?.profilePicture ? userInitials : ''}
              size="md"
              withFocus
              onClick={() => setIsSlideDownOpen(!isSlideDownOpen)}
            />
            <SlideDown
              title={
                <FlexBox direction="column" gap="4px" alignItems="flex-start">
                  <Typography fontWeight={500}>{userName ?? ''}</Typography>
                  <Typography color={'var(--colors-secondary)'}>
                    {userData?.email ?? ''}
                  </Typography>
                </FlexBox>
              }
              bodySections={[
                {
                  id: 'account-settings',
                  content: (
                    <div className={styles.slideDownBody}>
                      {dropmenuItems
                        .flat()
                        .slice(0, -1)
                        .map((item) => (
                          <Link
                            key={item.key}
                            text={item.text}
                            preIcon={item.icon}
                            onClick={item.onClick}
                            color={'black'}
                          />
                        ))}
                    </div>
                  ),
                },
                {
                  id: 'logout',
                  content: (
                    <div className={styles.slideDownBody}>
                      {dropmenuItems
                        .flat()
                        .slice(-1)
                        .map((item) => (
                          <Link
                            key={item.key}
                            text={item.text}
                            preIcon={item.icon}
                            onClick={item.onClick}
                            color={'black'}
                          />
                        ))}
                    </div>
                  ),
                },
              ]}
              open={isSlideDownOpen}
              onOpenChange={setIsSlideDownOpen}
            />
          </>
        )}
      </div>
    </div>
  );
};
